import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, AlertTriangle, Eye, Target, Activity } from 'lucide-react';
import { statsApi, auditApi } from '../utils/api';
import { useApp } from '../context/AppContext';
import { formatTimestamp } from '../utils/constants';

const StatCard = ({ icon: Icon, label, value, subtext, trend, color = 'cyan' }) => {
  const colorMap = {
    cyan: 'text-cyan-400 bg-cyan-500/10 border-cyan-500/30',
    amber: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
    red: 'text-red-400 bg-red-500/10 border-red-500/30',
    emerald: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30'
  };

  return (
    <div className={`p-4 rounded-lg border ${colorMap[color]} backdrop-blur-sm`}>
      <div className="flex items-start justify-between mb-2">
        <div className={`p-2 rounded-lg ${colorMap[color].split(' ').slice(1).join(' ')}`}>
          <Icon className={`w-4 h-4 ${colorMap[color].split(' ')[0]}`} />
        </div>
        {trend && (
          <div className={`flex items-center gap-1 text-xs ${trend > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            {trend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            <span>{Math.abs(trend)}%</span>
          </div>
        )}
      </div>
      <p className="text-2xl font-mono font-bold text-white mb-1">{value}</p>
      <p className="text-xs font-mono uppercase tracking-wider text-slate-500">{label}</p>
      {subtext && <p className="text-[10px] text-slate-600 mt-1">{subtext}</p>}
    </div>
  );
};

const IssueItem = ({ title, description, severity }) => {
  const severityColors = {
    high: 'border-red-500/50 bg-red-500/10',
    medium: 'border-amber-500/50 bg-amber-500/10',
    low: 'border-slate-500/50 bg-slate-500/10'
  };
  
  const severityText = {
    high: 'text-red-400',
    medium: 'text-amber-400',
    low: 'text-slate-400'
  };

  return (
    <div className={`p-3 rounded-lg border ${severityColors[severity]}`}>
      <div className="flex items-start gap-2">
        <AlertTriangle className={`w-4 h-4 mt-0.5 ${severityText[severity]}`} />
        <div>
          <p className="text-sm text-white font-medium">{title}</p>
          <p className="text-xs text-slate-500 mt-0.5">{description}</p>
        </div>
      </div>
    </div>
  );
};

export const IntelligencePanel = () => {
  const { refreshKey } = useApp();
  const [stats, setStats] = useState(null);
  const [recentActions, setRecentActions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsData, logsData] = await Promise.all([
          statsApi.get(),
          auditApi.getAll({ limit: 5 })
        ]);
        setStats(statsData);
        setRecentActions(logsData);
      } catch (error) {
        console.error('Failed to fetch intelligence data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [refreshKey]);

  if (loading) {
    return (
      <div className="h-full flex flex-col gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-24 bg-slate-900/40 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  const readyRate = stats?.total_aeds > 0 
    ? ((stats.status_counts?.READY || 0) / stats.total_aeds * 100).toFixed(1)
    : 0;

  return (
    <div className="h-full flex flex-col gap-4 overflow-y-auto custom-scrollbar">
      {/* Header */}
      <div className="glass rounded-lg p-4 tech-corner">
        <div className="flex items-center gap-2 mb-2">
          <Target className="w-4 h-4 text-cyan-400" />
          <h2 className="font-heading text-sm font-semibold uppercase tracking-wide text-white">
            Decision Intelligence
          </h2>
        </div>
        <p className="text-xs text-slate-500">Real-time system insights and recommendations</p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 gap-3">
        <StatCard
          icon={Activity}
          label="Ready Rate"
          value={`${readyRate}%`}
          subtext="Target: 95%"
          color="emerald"
        />
        <StatCard
          icon={AlertTriangle}
          label="Reposition"
          value={`${stats?.reposition_rate || 0}%`}
          subtext="Target: <3%"
          color={stats?.reposition_rate > 3 ? 'red' : 'amber'}
        />
        <StatCard
          icon={Eye}
          label="Needs Review"
          value={stats?.review_required || 0}
          subtext="Human validation required"
          color="amber"
        />
        <StatCard
          icon={Target}
          label="Low Confidence"
          value={stats?.low_confidence_count || 0}
          subtext="Below 85% threshold"
          color={stats?.low_confidence_count > 5 ? 'red' : 'cyan'}
        />
      </div>

      {/* Top Issues */}
      <div className="glass rounded-lg p-4 flex-1">
        <h3 className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-3">
          Top Issues Today
        </h3>
        <div className="space-y-2">
          {(stats?.review_required || 0) > 0 && (
            <IssueItem
              title={`${stats.review_required} AEDs Need Review`}
              description="Low confidence scores require human validation"
              severity="medium"
            />
          )}
          {(stats?.status_counts?.REPOSITION || 0) > 0 && (
            <IssueItem
              title={`${stats.status_counts.REPOSITION} Camera Repositions Needed`}
              description="LED not visible - camera alignment issue"
              severity="high"
            />
          )}
          {(stats?.status_counts?.AED_NOT_PRESENT || 0) > 0 && (
            <IssueItem
              title={`${stats.status_counts.AED_NOT_PRESENT} AEDs Not Present`}
              description="Cabinet appears empty - immediate attention"
              severity="high"
            />
          )}
          {(stats?.status_counts?.LOST_CONTACT || 0) > 0 && (
            <IssueItem
              title={`${stats.status_counts.LOST_CONTACT} Units Lost Contact`}
              description="No recent telemetry - check connectivity"
              severity="low"
            />
          )}
          {stats?.review_required === 0 && stats?.status_counts?.REPOSITION === 0 && (
            <div className="p-4 rounded-lg border border-emerald-500/30 bg-emerald-500/10 text-center">
              <p className="text-sm text-emerald-400">All systems operational</p>
              <p className="text-xs text-slate-500 mt-1">No critical issues detected</p>
            </div>
          )}
        </div>
      </div>

      {/* Recent Actions */}
      <div className="glass rounded-lg p-4">
        <h3 className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-3">
          Recent Actions
        </h3>
        {recentActions.length > 0 ? (
          <div className="space-y-2">
            {recentActions.map((action, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-slate-800/50 last:border-0">
                <div>
                  <p className="text-xs text-white font-mono">{action.aed_id}</p>
                  <p className="text-[10px] text-slate-500">{action.action}</p>
                </div>
                <span className="text-[10px] text-slate-600">
                  {formatTimestamp(action.timestamp)}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-slate-600 text-center py-4">No recent actions</p>
        )}
      </div>
    </div>
  );
};
